import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import { Edit, Delete, Search } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import PaginationData from 'types/models/PaginationData';
import FiservQrSubsidiarySalesStand from 'types/models/FiservQrSubsidiarySalesStand';
import EditFiservQrSubsidiaryConfigContext from 'contexts/EditFiservQrSubsidiaryConfigContext';
import AppContext from 'contexts/AppContext';
import {
  getFiservQrSubsidiarySaleStands,
  deleteFiservQrSubsidiarySalesStand,
  findCloverPointsByAssignedSubsidiary,
} from 'services/fiservQrServices';
import ConfirmDialog from 'components/ConfirmDialog';

export default (): JSX.Element => {
  const history = useHistory();
  const [paginationFiservQrSubsidiarySalesStand, setPaginationFiservQrSubsidiarySalesStand] =
    useState<PaginationData<FiservQrSubsidiarySalesStand> | null>(null);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [fiservQrSubsidiarySalesStandIdToDelete, setFiservQrSubsidiarySalesStandIdToDelete] = React.useState<
    string | null
  >(null);
  const [saleStandSearchText, setSaleStandSearchText] = React.useState('');
  const { currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const {
    selectedFiservQrSubsidiaryConfig,
    setSelectedFiservQrSubsidiarySalesStand,
    setPossibleCloverPointsToAssignToSaleStands,
  } = React.useContext(EditFiservQrSubsidiaryConfigContext);
  const { t } = useTranslation();

  const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);

  const goToPage = (url: string): void => {
    history.push(url);
  };

  const onCloseDialog = () => {
    setFiservQrSubsidiarySalesStandIdToDelete(null);
  };

  React.useEffect(() => {
    if (currentPageListNumber && selectedFiservQrSubsidiaryConfig) {
      loadData(currentPageListNumber);
    } else {
      history.push('/private/admin/payments/fiserv-qr');
    }
  }, [currentPageListNumber]);

  const handleSearchTerm = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSaleStandSearchText(event.target.value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      loadSearchData(event.target.value);
    }, 500);
  };

  const loadData = React.useCallback(async (page?: number) => {
    if (!selectedFiservQrSubsidiaryConfig) return;
    const paginationData = await getFiservQrSubsidiarySaleStands(selectedFiservQrSubsidiaryConfig.id, page);
    if (paginationData) setPaginationFiservQrSubsidiarySalesStand(paginationData);
    const possibleCloverPoints = await findCloverPointsByAssignedSubsidiary(selectedFiservQrSubsidiaryConfig.id);
    if (possibleCloverPoints) setPossibleCloverPointsToAssignToSaleStands(possibleCloverPoints);
  }, []);

  const loadSearchData = React.useCallback(async (saleStandsSearchTerm: string, page?: number) => {
    if (!selectedFiservQrSubsidiaryConfig) return;
    const paginationData = await getFiservQrSubsidiarySaleStands(
      selectedFiservQrSubsidiaryConfig.id,
      page,
      saleStandsSearchTerm,
    );
    if (paginationData) setPaginationFiservQrSubsidiarySalesStand(paginationData);
    const possibleCloverPoints = await findCloverPointsByAssignedSubsidiary(selectedFiservQrSubsidiaryConfig.id);
    if (possibleCloverPoints) setPossibleCloverPointsToAssignToSaleStands(possibleCloverPoints);
  }, []);

  const onDeleteFiservQrSubsidiarySalesConfig = async () => {
    if (selectedFiservQrSubsidiaryConfig && fiservQrSubsidiarySalesStandIdToDelete) {
      await deleteFiservQrSubsidiarySalesStand(
        selectedFiservQrSubsidiaryConfig?.id,
        fiservQrSubsidiarySalesStandIdToDelete,
      );
      loadData();
    }
  };

  const renderEditButton = (fiservQrSubsidiarySalesStand: FiservQrSubsidiarySalesStand): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          setSelectedFiservQrSubsidiarySalesStand(fiservQrSubsidiarySalesStand);
          history.push('sales-stands/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderDeleteButton = (fiservQrSubsidiarySalesStand: FiservQrSubsidiarySalesStand | null): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          if (!fiservQrSubsidiarySalesStand) return;
          setOpenDialog(true);
          setFiservQrSubsidiarySalesStandIdToDelete(fiservQrSubsidiarySalesStand.id);
        }}
      >
        <Delete />
      </IconButton>
    );
  };

  const renderContent = (paginationData: FiservQrSubsidiarySalesStand[]): JSX.Element => {
    return (
      <>
        {paginationData.map((fiservQrSubsidiarySalesStand: FiservQrSubsidiarySalesStand) => (
          <TableRow key={fiservQrSubsidiarySalesStand.id}>
            <TableCell component="th" scope="row">
              {fiservQrSubsidiarySalesStand.name}
            </TableCell>
            <TableCell component="th" scope="row">
              {fiservQrSubsidiarySalesStand.cloverPoint?.serial || '-'}
            </TableCell>
            <TableCell component="th" scope="row">
              {fiservQrSubsidiarySalesStand.cloverPoint?.description || '-'}
            </TableCell>
            <TableCell>{renderEditButton(fiservQrSubsidiarySalesStand)}</TableCell>
            <TableCell>{renderDeleteButton(fiservQrSubsidiarySalesStand)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <div>
      <ConfirmDialog
        open={openDialog && fiservQrSubsidiarySalesStandIdToDelete !== null}
        setOpen={setOpenDialog}
        onConfirm={onDeleteFiservQrSubsidiarySalesConfig}
        onCancel={onCloseDialog}
      />
      <div className="search-container">
        <TextField
          id="search"
          name="searchValue"
          variant="outlined"
          label={t('search-label')}
          value={saleStandSearchText}
          onChange={handleSearchTerm}
          margin="dense"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
            style: {
              borderRadius: '10px',
              backgroundColor: '#fff',
            },
          }}
        />
      </div>
      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="primary" onClick={() => goToPage('sales-stands/create')}>
          {`${t('listFiservQrSubsidiarySaleStandConfigs-page-create')} ${selectedFiservQrSubsidiaryConfig?.name}`}
        </Button>
      </div>
      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="secondary" onClick={() => history.go(-1)}>
          {t('go-back')}
        </Button>
      </div>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listFiservQrSubsidiarySaleStandConfigs-page-name')}</TableCell>
              <TableCell>{t('listCloverPoint-page-serial')}</TableCell>
              <TableCell>{t('listFiservQrSubsidiarySaleStandConfigs-page-clover-point')}</TableCell>
              <TableCell>{t('listFiservQrSubsidiarySaleStandConfigs-page-edit')}</TableCell>
              <TableCell>{t('listFiservQrSubsidiarySaleStandConfigs-page-delete')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationFiservQrSubsidiarySalesStand?.data && paginationFiservQrSubsidiarySalesStand?.data.length > 0 ? (
              renderContent(paginationFiservQrSubsidiarySalesStand.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listCloverPoints-page-noPoints')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationFiservQrSubsidiarySalesStand && (
        <TablePagination
          component="div"
          count={paginationFiservQrSubsidiarySalesStand.total}
          page={paginationFiservQrSubsidiarySalesStand.currentPage - 1}
          onPageChange={(_, page) => setCurrentPageListNumber(page + 1)}
          rowsPerPage={paginationFiservQrSubsidiarySalesStand.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </div>
  );
};
